/** @format */

import React, { FC } from "react";
import {
  Navigate,
  RouterProvider,
  createBrowserRouter,
  useLocation,
} from "react-router-dom";
import { useLocale } from "./features/i18n/hooks";
import App from "./App";
import LocaleWrapper from "./features/i18n/LocaleWrapper";
import { Locale, locales } from "./features/i18n/init";
import resourcesRoutes from "./features/resources/resourcesRoutes";
import devRoutes from "./features/developer/developerRoutes";
import themeRoutes from "./features/theme/themeRoutes";
import authRoutes from "./auth/authRoutes";
import Home from "./features/home/Home";
import userResourcesRoutes from "./features/userResources/userResourcesRoutes";

const RedirectToLocale: React.FC<{ currentLocale: Locale }> = ({
  currentLocale,
}) => {
  const location = useLocation();
  return (
    <Navigate
      to={`/${currentLocale}${location.pathname}${location.search}${location.hash}`}
    />
  );
};

const AppRouter: FC = () => {
  const currentLocale = useLocale();
  const router = createBrowserRouter([
    {
      path: "/",
      errorElement: <App />,
      element: <App />,
      children: [
        {
          index: true,
          element: <Navigate to={`/${currentLocale}`} />,
        },
        ...locales.map((locale) => ({
          path: locale,
          element: (
            <>
              <LocaleWrapper key={locale} locale={locale} />
              <Home />
            </>
          ),
        })),
        ...locales.map((locale) => ({
          path: `${locale}/*`,
          element: <LocaleWrapper key={locale} locale={locale} />,
          children: [
            /** Load Resources routes */
            ...resourcesRoutes,

            /** Load User Resources Routes */
            ...userResourcesRoutes,

            /** Load extra routes */
            {
              path: "dev",
              children: devRoutes,
            },
            {
              path: "theme",
              children: themeRoutes,
            },
            {
              path: "auth",
              children: authRoutes,
            },
          ],
        })),
      ],
    },
    {
      path: "*",
      element: <RedirectToLocale currentLocale={currentLocale} />,
    },
  ]);

  return <RouterProvider router={router} />;
};

export default AppRouter;
